<template>
  <ReportCellWrapper
    :trial-over="params.data?.trialOver"
    :is-disabled-by-tier="params.data?.isDisabledByTier"
  >
    <div v-if="params.data?.isFolder"> -- </div>
    <component
      v-else
      :is="
        !isGeneralAccessReport &&
        (reportVisibility === REPORT_VISIBILITY.SHARED ||
          reportVisibility === REPORT_VISIBILITY.EXTERNALLY_SHARED)
          ? BittsTooltip
          : 'div'
      "
      :mount-to-body="true"
      placement="bottom"
      data-testid="report-visibility-info"
      overlay-class="report-access-cell__user-tooltip"
      class="flex items-center gap-4"
      default-slot-classes="flex items-center gap-4"
    >
      <FontAwesomeIcon
        :icon="
          isGeneralAccessReport
            ? ['fak', 'company']
            : reportVizToInfo[reportVisibility].icon
        "
        class="text-neutral-accent h-14 w-14"
      />
      <span class="max-w-full truncate">
        {{
          isGeneralAccessReport
            ? currentOrg.name
            : reportVizToInfo[reportVisibility].text
        }}
      </span>
      <BittsTooltip
        v-if="isGeneralAccessReport && ownOrgReportUsers.length > 0"
        :mount-to-body="true"
        placement="bottom"
        overlay-class="report-access-cell__user-tooltip"
        data-testid="report-access-ga-overflow"
      >
        <span class="text-neutral-text">+{{ ownOrgReportUsers.length }}</span>
        <template #title>
          <ReportAccessCellUserList :users="ownOrgReportUsers" />
        </template>
      </BittsTooltip>
      <template #title>
        <ReportAccessCellUserList :users="reportUsers" />
      </template>
    </component>
    <component
      v-if="isPartnerGeneralAccessReport && externalReportOrg"
      :is="partnerOrgReportUsers.length > 0 ? BittsTooltip : 'div'"
      :mount-to-body="true"
      placement="bottom"
      data-testid="report-access-partner-ga"
      overlay-class="report-access-cell__user-tooltip"
      class="flex items-center gap-4 text-sm text-neutral-text-weak"
      default-slot-classes="flex items-center gap-4 text-sm text-neutral-text-weak"
    >
      <FontAwesomeIcon
        :icon="['fak', 'company']"
        class="text-neutral-accent h-14 w-14"
      />
      <span class="max-w-full truncate">{{ externalReportOrg.name }}</span>
      <span v-if="partnerOrgReportUsers.length > 0" class="text-neutral-text">
        +{{ partnerOrgReportUsers.length }}
      </span>
      <template #title>
        <ReportAccessCellUserList :users="partnerOrgReportUsers" />
      </template>
    </component>
    <span
      v-else-if="reportVizToInfo[reportVisibility].externalText"
      data-testid="report-access-external-text"
      class="text-sm text-neutral-text-weak max-w-full truncate"
    >
      {{ reportVizToInfo[reportVisibility].externalText }}
    </span>
  </ReportCellWrapper>
</template>

<script setup lang="ts">
import { BittsTooltip } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { pluralize } from 'humanize-plus';
import { computed } from 'vue';

import ReportAccessCellUserList from '@/components/reports/ReportAccessCellUserList.vue';
import ReportCellWrapper from '@/components/reports/ReportCellWrapper.vue';

import useAuth from '@/composables/useAuth';
import { GENERAL_ACCESS_ONLY_INVITED } from '@/constants/reports';
import { usePartnersStore, useTeamStore } from '@/stores';
import { ReportCell, ReportUserAccess } from '@/types/reports';
import { User } from '@/types/root';
import {
  SharedList,
  SharedListOrg,
  SharedListUser,
} from '@/types/shared_lists';

const { params } = defineProps<{ params: ICellRendererParams<ReportCell> }>();

const REPORT_VISIBILITY = {
  PUBLIC: 'Public',
  PRIVATE: 'Private',
  SHARED: 'Shared',
  EXTERNALLY_SHARED: 'externally-shared',
} as const;
type ReportVisibility =
  (typeof REPORT_VISIBILITY)[keyof typeof REPORT_VISIBILITY];

const { currentOrg, currentUser } = useAuth();
const { getAuthorizationByUserId } = useTeamStore();
const { getPartnerOrgById } = usePartnersStore();

const reportUsers = computed(() => {
  let users: ReportUserAccess[] = [];
  if (params.data?.isFolder) return [];
  if (!params.data?.isStaticList) {
    if (!params.data?.report?.user_access) {
      return [];
    }
    users = params.data?.report?.user_access.map(
      (userAccess: ReportUserAccess): ReportUserAccess => {
        const user =
          userAccess.user_id === currentUser.value?.id
            ? currentUser.value
            : userAccess.user ||
              getAuthorizationByUserId(userAccess.user_id)?.user ||
              ({ first_name: 'Report User' } as User);
        return { ...userAccess, user };
      },
    );
  } else {
    users = params.data?.report?.permissions.flatMap(
      (permission: SharedListOrg) =>
        permission.users.map((listUser: SharedListUser): ReportUserAccess => {
          const user =
            listUser.user_id === currentUser.value?.id
              ? currentUser.value
              : ({
                  id: listUser.user_id,
                  first_name: listUser.first_name || 'Report',
                  last_name: listUser.last_name || 'User',
                  email: listUser.email,
                  picture_url: listUser.picture_url,
                } as User);
          return {
            user_id: listUser.user_id,
            organization_id: permission.organization_id,
            access_level: 'viewer',
            user,
          };
        }),
    );
  }
  // Sort users: report owner first, then others alphabetically by first name
  return users.sort((a: ReportUserAccess, b: ReportUserAccess) => {
    // Check if user is the report owner (current user)
    const aIsOwner = a.user_id === currentUser.value?.id;
    const bIsOwner = b.user_id === currentUser.value?.id;

    // Report owner should come first
    if (aIsOwner && !bIsOwner) return -1;
    if (!aIsOwner && bIsOwner) return 1;

    // If both are owners or both are not owners, sort alphabetically by first name
    const aFirstName = a.user?.first_name || 'Report User';
    const bFirstName = b.user?.first_name || 'Report User';
    return aFirstName.localeCompare(bFirstName);
  });
});
const isCurrentUserInReport = computed(() => {
  if (params.data?.isFolder) return false;
  if (params.data?.isStaticList) {
    return params.data?.report?.permissions.some((permission: SharedListOrg) =>
      permission.users.some(
        (user: SharedListUser) => user.user_id === currentUser.value?.id,
      ),
    );
  }
  return params.data?.report?.user_access?.some(
    (userAccess: ReportUserAccess) =>
      userAccess.user_id === currentUser.value?.id,
  );
});
const externalReportOrg = computed(() => {
  if (params.data?.isStaticList || params.data?.isFolder) return null;
  const report = params.data?.report;
  if (report?.shared_partner_org_id == null) return null;
  const partnerOrgId =
    report.shared_partner_org_id !== currentOrg.value?.id
      ? report.shared_partner_org_id
      : report.shared_by_partner_org_id;
  if (!partnerOrgId) return null;
  return getPartnerOrgById(partnerOrgId);
});

const isGeneralAccessReport = computed(() => {
  if (params.data?.isStaticList || params.data?.isFolder) return false;
  const ga = params.data?.report?.general_access;
  return ga != null && ga !== GENERAL_ACCESS_ONLY_INVITED;
});

const isPartnerGeneralAccessReport = computed(() => {
  if (params.data?.isStaticList || params.data?.isFolder) return false;
  const pga = params.data?.report?.partner_general_access;
  return pga != null && pga !== GENERAL_ACCESS_ONLY_INVITED;
});

const ownOrgReportUsers = computed(() =>
  reportUsers.value.filter(
    (u) => !u.organization_id || u.organization_id === currentOrg.value?.id,
  ),
);

const partnerOrgReportUsers = computed(() =>
  reportUsers.value.filter(
    (u) =>
      u.organization_id != null && u.organization_id !== currentOrg.value?.id,
  ),
);

const reportVisibility = computed<ReportVisibility>(() => {
  if (params.data?.isStaticList || params.data?.isFolder) {
    return REPORT_VISIBILITY.EXTERNALLY_SHARED;
  }
  if (params.data?.report?.visibility === 'public') {
    return REPORT_VISIBILITY.PUBLIC;
  }
  if (params.data?.report?.shared_partner_org_id != null) {
    return REPORT_VISIBILITY.EXTERNALLY_SHARED;
  }
  const userAccessLength = params.data?.report?.user_access?.length || 0;
  if (
    userAccessLength > 1 ||
    (userAccessLength === 1 && !isCurrentUserInReport.value)
  ) {
    return REPORT_VISIBILITY.SHARED;
  }
  // 0 users or 1 user who is the current user
  return REPORT_VISIBILITY.PRIVATE;
});

const numPeople = computed(() => {
  if (params.data?.isFolder) return 0;
  if (params.data?.isStaticList) {
    return params.data?.report?.permissions.reduce(
      (acc: number, curr: SharedList['permissions'][0]) => {
        acc += curr.users.length;
        return acc;
      },
      0,
    );
  }
  return params.data?.report?.user_access?.length || 0;
});
function getExternalText(): string {
  if (params.data?.isStaticList) {
    const externalOrg = params.data?.report?.permissions.find(
      (permission: SharedListOrg) =>
        permission.organization_id !== currentOrg.value?.id,
    );
    const numExternalUsers = externalOrg?.users.length || 0;
    if (!numExternalUsers) return '';
    return `${numExternalUsers} from ${externalOrg?.name || 'Partner'}`;
  }
  if (!externalReportOrg.value || !params.data || params.data.isFolder)
    return '';
  const numExternalUsers =
    params.data.report.user_access?.filter(
      (entry: ReportUserAccess) =>
        entry.organization_id != null &&
        entry.organization_id !== currentOrg.value?.id,
    ).length || 0;
  if (!numExternalUsers) return '';
  return `${numExternalUsers} from ${externalReportOrg.value.name || 'Partner'}`;
}
const reportVizToInfo = computed<
  Record<
    ReportVisibility,
    {
      icon: string[];
      text: string;
      externalText?: string;
    }
  >
>(() => ({
  [REPORT_VISIBILITY.PUBLIC]: {
    icon: ['fak', 'company'],
    text: currentOrg.value?.name || 'Public',
  },
  [REPORT_VISIBILITY.PRIVATE]: {
    icon: ['fak', 'lock'],
    text: 'Private',
  },
  [REPORT_VISIBILITY.SHARED]: {
    icon: ['fak', 'team'],
    text: `${numPeople.value} ${pluralize(numPeople.value, 'Person', 'People')}`,
  },
  [REPORT_VISIBILITY.EXTERNALLY_SHARED]: {
    icon: ['fak', 'partner'],
    text: `${numPeople.value} ${pluralize(numPeople.value, 'Person', 'People')}`,
    externalText: getExternalText(),
  },
}));
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.report-access-cell__user-avatar {
  .bitts-avatar-item__text-title {
    color: white !important;
    font-weight: normal !important;
    white-space: nowrap;
  }
}
.report-access-cell__user-tooltip {
  .ant-tooltip-inner {
    max-width: 200px !important;
  }
}
</style>
